/** The master list of all languages, keyed by language code. */
const languages = {
    af: { code: 'af', name: 'Afrikaans' },
    ar: { code: 'ar', name: 'Arabic' },
    bg: { code: 'bg', name: 'Bulgarian' },
    bn: { code: 'bn', name: 'Bengali' },
    // ca: { code: 'ca', name: 'Catalan, Valencian' },
    cs: { code: 'cs', name: 'Czech' },
    cy: { code: 'cy', name: 'Welsh' },
    da: { code: 'da', name: 'Danish' },
    de: { code: 'de', name: 'German' },
    el: { code: 'el', name: 'Greek' },
    en: { code: 'en', name: 'English' },
    es: { code: 'es', name: 'Spanish' },
    et: { code: 'et', name: 'Estonian' },
    fa: { code: 'fa', name: 'Persian' },
    fi: { code: 'fi', name: 'Finnish' },
    fr: { code: 'fr', name: 'French' },
    gu: { code: 'gu', name: 'Gujarati' },
    he: { code: 'he', name: 'Hebrew' },
    hi: { code: 'hi', name: 'Hindi' },
    hr: { code: 'hr', name: 'Croatian' },
    hu: { code: 'hu', name: 'Hungarian' },
    id: { code: 'id', name: 'Indonesian' },
    is: { code: 'is', name: 'Icelandic' },
    it: { code: 'it', name: 'Italian' },
    ja: { code: 'ja', name: 'Japanese' },
    kn: { code: 'kn', name: 'Kannada' },
    ko: { code: 'ko', name: 'Korean' },
    lt: { code: 'lt', name: 'Lithuanian' },
    lv: { code: 'lv', name: 'Latvian' },
    mk: { code: 'mk', name: 'Macedonian' },
    ml: { code: 'ml', name: 'Malayalam' },
    mr: { code: 'mr', name: 'Marathi' },
    ne: { code: 'ne', name: 'Nepali' },
    nl: { code: 'nl', name: 'Dutch' },
    no: { code: 'no', name: 'Norwegian' },
    pa: { code: 'pa', name: 'Punjabi' },
    pl: { code: 'pl', name: 'Polish' },
    pt: { code: 'pt', name: 'Portuguese' },
    ro: { code: 'ro', name: 'Romanian' },
    ru: { code: 'ru', name: 'Russian' },
    sk: { code: 'sk', name: 'Slovak' },
    sl: { code: 'sl', name: 'Slovenian' },
    so: { code: 'so', name: 'Somali' },
    sq: { code: 'sq', name: 'Albanian' },
    sr: { code: 'sr', name: 'Serbian' },
    sv: { code: 'sv', name: 'Swedish' },
    sw: { code: 'sw', name: 'Swahili' },
    ta: { code: 'ta', name: 'Tamil' },
    te: { code: 'te', name: 'Telugu' },
    th: { code: 'th', name: 'Thai' },
    tl: { code: 'tl', name: 'Tagalog' },
    tr: { code: 'tr', name: 'Turkish' },
    uk: { code: 'uk', name: 'Ukrainian' },
    ur: { code: 'ur', name: 'Urdu' },
    vi: { code: 'vi', name: 'Vietnamese' },
    zh: { code: 'zh', name: 'Chinese (Simplified)' },
    'zh-Hant': { code: 'zh-Hant', name: 'Chinese (Traditional)' },
};
/** The default language code used across features regardless of list. */
const defaultLanguageCode = 'en';

/** Looks up a language's display name by code from the master list. */
const getLanguageName = (code) => languages[code]?.name;

/** Maps language codes to their master-list entries, sorted by name. */
const toSortedLanguages = (codes) => codes
    .map((code) => languages[code])
    .sort((a, b) => a.name.localeCompare(b.name));

/**
 * The conversation language codes.
 *
 * Generally corresponds to the languages supported by language-detection.
 *
 * Used by:
 * - Message translation.
 * - The conversation language picker and copilot preferred-language settings.
 * - Template translations.
 *
 * Each must exist in the master list.
 **/
const conversationLanguageCodes = [
    'ar',
    'bg',
    'bn',
    // 'ca',
    'cs',
    'cy',
    'da',
    'de',
    'el',
    'en',
    'es',
    'et',
    'fa',
    'fi',
    'fr',
    'gu',
    'he',
    'hi',
    'hr',
    'hu',
    'id',
    'is',
    'it',
    'ja',
    'ko',
    'lt',
    'lv',
    'mk',
    'mr',
    'nl',
    'no',
    'pa',
    'pl',
    'pt',
    'ro',
    'ru',
    'sk',
    'sl',
    'so',
    'sq',
    'sr',
    'sv',
    'sw',
    'ta',
    'te',
    'th',
    'tl',
    'tr',
    'uk',
    'ur',
    'vi',
    'zh',
    'zh-Hant',
];
/** The default conversation language code. */
const defaultConversationLanguageCode = 'en';
/** The conversation languages. */
const conversationLanguages = toSortedLanguages(conversationLanguageCodes);

/**
 * A limited set of languages supported by many features.
 * This list was extracted from 'app/utils/supportedLanguages'.
 * This has historically been mistaken for the full conversation-supported languages,
 * due to the name suggesting it to be master list.
 * It was renamed to 'limitedSupportLanguages' in order to clarify that
 * it is not the full list of supported languages, and to somewhat discourage its use.
 *
 * Each code must exist in the master list.
 *
 * WARNING: csat-languages.ts is a superset of this list.
 */
const limitedSupportLanguageCodes = [
    'ar',
    'bg',
    'cs',
    'cy',
    'da',
    'de',
    'el',
    'en',
    'es',
    'et',
    'fi',
    'fr',
    'he',
    'hr',
    'hu',
    'is',
    'it',
    'ja',
    'ko',
    'lt',
    'lv',
    'nl',
    'no',
    'pl',
    'pt',
    'ro',
    'ru',
    'sk',
    'sl',
    'sr',
    'sv',
    'th',
    'tr',
    'uk',
    'zh',
    'zh-Hant',
];
/** The limited-support languages. */
const limitedSupportLanguages = toSortedLanguages(limitedSupportLanguageCodes);

/**
 * Language codes supported for CSAT that are not part of the limited support
 * languages list. Each must exist in the master list.
 */
const csatSpecificLanguageCodes = [
    'sq',
    'id',
    'fa',
    'vi',
];
/**
 * CSAT language codes: the limited support codes plus the CSAT-specific
 * additions, deduplicated by code so an addition that also enters the limited
 * support list cannot silently produce a duplicate entry.
 */
const csatLanguageCodes = [
    ...new Set([
        ...limitedSupportLanguageCodes,
        ...csatSpecificLanguageCodes,
    ]),
];
/** The CSAT languages, sorted by name. */
const csatLanguages = toSortedLanguages(csatLanguageCodes);

/**
 * The master list contains all supported languages, but some of them are still
 * not translated on Crowdin. This list is used to avoid enabling an
 * untranslated language in a Messenger. To see the latest translated languages,
 * see
 * https://github.com/dixahq/messenger-widget/blob/develop/src/shared/language/translatedLocales.ts
 */
const messengerTranslatedLanguageCodes = [
    'ar',
    'bg',
    'cs',
    'cy',
    'da',
    'de',
    'el',
    'en',
    'es',
    'et',
    'fi',
    'fr',
    'he',
    'hr',
    'hu',
    'is',
    'it',
    'ja',
    'ko',
    'lt',
    'lv',
    'nl',
    'no',
    'pl',
    'pt',
    'ro',
    'ru',
    'sk',
    'sl',
    'sr',
    'sv',
    'th',
    'tr',
    'uk',
    'zh',
    'zh-Hant',
];
const messengerTranslatedLanguageCodeSet = new Set(messengerTranslatedLanguageCodes);
/** Whether the code is a translated messenger language. */
const isMessengerTranslatedLanguage = (code) => typeof code === 'string' && messengerTranslatedLanguageCodeSet.has(code);
/** The translated messenger languages. */
const messengerTranslatedLanguages = toSortedLanguages(messengerTranslatedLanguageCodes);

export { conversationLanguages, csatLanguages, defaultConversationLanguageCode, defaultLanguageCode, getLanguageName, isMessengerTranslatedLanguage, limitedSupportLanguages, messengerTranslatedLanguages };
//# sourceMappingURL=index.es.js.map
